import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, Shield, Activity, Clock, AlertTriangle, Ban, 
  Unlock, Loader, X, RefreshCw, Eye, LogOut, Globe, Monitor,
  Smartphone, Tablet, TrendingUp, Users, Lock, FileText, Calendar
} from 'lucide-react';
import adminService, { 
  AuditLog, AuditLogListResponse, LoginHistory, LoginHistoryListResponse,
  AdminSession, AdminSessionListResponse, BlockedIP, BlockedIPListResponse,
  SecurityStatistics, FailedLoginAttempt, FailedLoginAttemptListResponse,
  GetAuditLogsParams, GetLoginHistoryParams, GetAdminSessionsParams,
  GetFailedLoginAttemptsParams, BlockIPRequest, UnblockIPRequest, RevokeSessionRequest
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';

type TabType = 'overview' | 'audit-logs' | 'login-history' | 'sessions' | 'blocked-ips' | 'failed-logins';

const SecurityAuditManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Security Statistics
  const [securityStats, setSecurityStats] = useState<SecurityStatistics | null>(null);

  // Audit Logs
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditLogsPage, setAuditLogsPage] = useState(1);
  const [auditLogsTotalPages, setAuditLogsTotalPages] = useState(1);
  const [auditLogsTotal, setAuditLogsTotal] = useState(0);
  const [auditLogsFilters, setAuditLogsFilters] = useState<GetAuditLogsParams>({
    page: 1,
    limit: 20,
  });

  // Login History
  const [loginHistory, setLoginHistory] = useState<LoginHistory[]>([]);
  const [loginHistoryPage, setLoginHistoryPage] = useState(1);
  const [loginHistoryTotalPages, setLoginHistoryTotalPages] = useState(1);
  const [loginHistoryTotal, setLoginHistoryTotal] = useState(0);
  const [loginHistoryFilters, setLoginHistoryFilters] = useState<GetLoginHistoryParams>({
    page: 1,
    limit: 20,
  });

  // Sessions
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsTotalPages, setSessionsTotalPages] = useState(1);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsFilters, setSessionsFilters] = useState<GetAdminSessionsParams>({
    page: 1,
    limit: 20,
    status: 'all',
  });

  // Blocked IPs
  const [blockedIPs, setBlockedIPs] = useState<BlockedIP[]>([]);
  const [blockedIPsPage, setBlockedIPsPage] = useState(1);
  const [blockedIPsTotalPages, setBlockedIPsTotalPages] = useState(1);
  const [blockedIPsTotal, setBlockedIPsTotal] = useState(0);
  const [blockIPModal, setBlockIPModal] = useState(false);
  const [unblockIPModal, setUnblockIPModal] = useState(false);
  const [selectedIP, setSelectedIP] = useState<string>('');
  const [blockIPForm, setBlockIPForm] = useState<BlockIPRequest>({
    ip_address: '',
    reason: '',
    block_duration_days: undefined,
  });
  const [processing, setProcessing] = useState(false);

  // Failed Login Attempts
  const [failedAttempts, setFailedAttempts] = useState<FailedLoginAttempt[]>([]);
  const [failedAttemptsPage, setFailedAttemptsPage] = useState(1);
  const [failedAttemptsTotalPages, setFailedAttemptsTotalPages] = useState(1);
  const [failedAttemptsTotal, setFailedAttemptsTotal] = useState(0);
  const [failedAttemptsFilters, setFailedAttemptsFilters] = useState<GetFailedLoginAttemptsParams>({
    page: 1,
    limit: 20,
  });

  useEffect(() => {
    fetchSecurityStats();
    if (activeTab === 'audit-logs') fetchAuditLogs();
    if (activeTab === 'login-history') fetchLoginHistory();
    if (activeTab === 'sessions') fetchSessions();
    if (activeTab === 'blocked-ips') fetchBlockedIPs();
    if (activeTab === 'failed-logins') fetchFailedAttempts();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'audit-logs') fetchAuditLogs();
  }, [auditLogsPage, auditLogsFilters]);

  useEffect(() => {
    if (activeTab === 'login-history') fetchLoginHistory();
  }, [loginHistoryPage, loginHistoryFilters]);

  useEffect(() => {
    if (activeTab === 'sessions') fetchSessions();
  }, [sessionsPage, sessionsFilters]);

  useEffect(() => {
    if (activeTab === 'blocked-ips') fetchBlockedIPs();
  }, [blockedIPsPage]);

  useEffect(() => {
    if (activeTab === 'failed-logins') fetchFailedAttempts();
  }, [failedAttemptsPage, failedAttemptsFilters]);

  const fetchSecurityStats = async () => {
    try {
      setLoading(true);
      const stats = await adminService.getSecurityStatistics();
      setSecurityStats(stats);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch security statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAuditLogs({
        ...auditLogsFilters,
        page: auditLogsPage,
      });
      setAuditLogs(data.logs);
      setAuditLogsPage(data.pagination.current_page);
      setAuditLogsTotalPages(data.pagination.total_pages);
      setAuditLogsTotal(data.pagination.total_logs);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchLoginHistory = async () => {
    try {
      setLoading(true);
      const data = await adminService.getLoginHistory({
        ...loginHistoryFilters,
        page: loginHistoryPage,
      });
      setLoginHistory(data.history);
      setLoginHistoryPage(data.pagination.current_page);
      setLoginHistoryTotalPages(data.pagination.total_pages);
      setLoginHistoryTotal(data.pagination.total_records);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch login history');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAdminSessions({
        ...sessionsFilters,
        page: sessionsPage,
      });
      setSessions(data.sessions);
      setSessionsPage(data.pagination.current_page);
      setSessionsTotalPages(data.pagination.total_pages);
      setSessionsTotal(data.pagination.total_sessions);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedIPs = async () => {
    try {
      setLoading(true);
      const data = await adminService.getBlockedIPs(blockedIPsPage, 20);
      setBlockedIPs(data.blocked_ips);
      setBlockedIPsPage(data.pagination.current_page);
      setBlockedIPsTotalPages(data.pagination.total_pages);
      setBlockedIPsTotal(data.pagination.total_ips);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch blocked IPs');
    } finally {
      setLoading(false);
    }
  };

  const fetchFailedAttempts = async () => {
    try {
      setLoading(true);
      const data = await adminService.getFailedLoginAttempts({
        ...failedAttemptsFilters,
        page: failedAttemptsPage,
      });
      setFailedAttempts(data.attempts);
      setFailedAttemptsPage(data.pagination.current_page);
      setFailedAttemptsTotalPages(data.pagination.total_pages);
      setFailedAttemptsTotal(data.pagination.total_attempts);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch failed login attempts');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockIP = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setProcessing(true);
      await adminService.blockIPAddress(blockIPForm);
      toast.success('IP address blocked successfully!');
      setBlockIPModal(false);
      setBlockIPForm({ ip_address: '', reason: '', block_duration_days: undefined });
      fetchBlockedIPs();
      fetchSecurityStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to block IP address');
    } finally {
      setProcessing(false);
    }
  };

  const handleUnblockIP = async () => {
    try {
      setProcessing(true);
      await adminService.unblockIPAddress({ ip_address: selectedIP });
      toast.success('IP address unblocked successfully!');
      setUnblockIPModal(false);
      setSelectedIP('');
      fetchBlockedIPs();
      fetchSecurityStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to unblock IP address');
    } finally {
      setProcessing(false);
    }
  };

  const handleRevokeSession = async (sessionId: number) => {
    if (!confirm('Are you sure you want to revoke this session?')) return;
    
    try {
      await adminService.revokeAdminSession({ session_id: sessionId });
      toast.success('Session revoked successfully!');
      fetchSessions();
      fetchSecurityStats();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to revoke session');
    }
  };

  const getDeviceIcon = (deviceType: string | null) => {
    if (!deviceType) return <Monitor size={16} />;
    switch (deviceType.toLowerCase()) {
      case 'mobile':
        return <Smartphone size={16} />;
      case 'tablet':
        return <Tablet size={16} />;
      default:
        return <Monitor size={16} />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
      case 'active':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Active</span>;
      case 'failed':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Failed</span>;
      case 'expired':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">Expired</span>;
      case 'revoked':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Revoked</span>;
      default:
        return <span className="px-2 py-1 bg-secondary-200 text-secondary-700 rounded-full text-xs font-semibold">{status}</span>;
    }
  };

  if (loading && activeTab === 'overview') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">Security & Audit Management</h1>
          <p className="text-secondary-600">Monitor security events, audit logs, and manage access</p>
        </div>
        <button
          onClick={() => {
            fetchSecurityStats();
            if (activeTab === 'audit-logs') fetchAuditLogs();
            if (activeTab === 'login-history') fetchLoginHistory();
            if (activeTab === 'sessions') fetchSessions();
            if (activeTab === 'blocked-ips') fetchBlockedIPs();
            if (activeTab === 'failed-logins') fetchFailedAttempts();
          }}
          disabled={refreshing}
          className="flex items-center space-x-2 px-4 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all disabled:opacity-50"
        >
          <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="border-b border-secondary-200">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <Shield size={20} />
              <span>Overview</span>
            </button>
            <button
              onClick={() => setActiveTab('audit-logs')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'audit-logs'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <FileText size={20} />
              <span>Audit Logs</span>
            </button>
            <button
              onClick={() => setActiveTab('login-history')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'login-history'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <Clock size={20} />
              <span>Login History</span>
            </button>
            <button
              onClick={() => setActiveTab('sessions')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'sessions'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <Activity size={20} />
              <span>Active Sessions</span>
            </button>
            <button
              onClick={() => setActiveTab('blocked-ips')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'blocked-ips'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <Ban size={20} />
              <span>Blocked IPs</span>
            </button>
            <button
              onClick={() => setActiveTab('failed-logins')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'failed-logins'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <AlertTriangle size={20} />
              <span>Failed Logins</span>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              {securityStats ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl shadow-soft p-6 border-l-4 border-red-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-secondary-600 text-sm font-medium">Blocked IPs</p>
                          <p className="text-2xl font-bold text-secondary-900 mt-1">{securityStats.blocked_ips_count}</p>
                        </div>
                        <Ban className="text-red-500" size={32} />
                      </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-soft p-6 border-l-4 border-green-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-secondary-600 text-sm font-medium">Active Sessions</p>
                          <p className="text-2xl font-bold text-secondary-900 mt-1">{securityStats.active_sessions}</p>
                        </div>
                        <Activity className="text-green-500" size={32} />
                      </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-soft p-6 border-l-4 border-yellow-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-secondary-600 text-sm font-medium">Failed Attempts (Today)</p>
                          <p className="text-2xl font-bold text-secondary-900 mt-1">{securityStats.today_failed_attempts}</p>
                        </div>
                        <AlertTriangle className="text-yellow-500" size={32} />
                      </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-soft p-6 border-l-4 border-blue-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-secondary-600 text-sm font-medium">Audit Logs (Today)</p>
                          <p className="text-2xl font-bold text-secondary-900 mt-1">{securityStats.today_audit_logs}</p>
                        </div>
                        <FileText className="text-blue-500" size={32} />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <AlertTriangle className="text-yellow-500 mb-4" size={48} />
                  <p className="text-secondary-600 text-lg font-semibold mb-2">Unable to load security statistics</p>
                  <p className="text-secondary-500 text-sm">Please try refreshing the page</p>
                </div>
              )}
            </>
          )}

          {/* Audit Logs Tab */}
          {activeTab === 'audit-logs' && (
            <div className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="animate-spin text-primary-500" size={32} />
                </div>
              ) : (
                <>
                  <div className="bg-white rounded-xl shadow-soft overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-secondary-50">
                          <tr>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Action</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Admin</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Resource</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">IP Address</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-secondary-200">
                          {auditLogs.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-6 py-12 text-center text-secondary-600">
                                No audit logs found
                              </td>
                            </tr>
                          ) : (
                            auditLogs.map((log) => (
                              <tr key={log.log_id} className="hover:bg-secondary-50 transition-colors">
                                <td className="px-6 py-4">
                                  <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold capitalize">
                                    {log.action.replace('_', ' ')}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <div>
                                    <p className="font-semibold text-secondary-900">{log.admin_name}</p>
                                    <p className="text-xs text-secondary-500">{log.admin_email}</p>
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <div>
                                    <p className="text-sm text-secondary-900 capitalize">{log.resource_type}</p>
                                    {log.resource_id && (
                                      <p className="text-xs text-secondary-500">ID: {log.resource_id}</p>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm text-secondary-600">{log.ip_address || 'N/A'}</span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm text-secondary-600">
                                    {new Date(log.created_at).toLocaleString()}
                                  </span>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    {auditLogsTotalPages > 1 && (
                      <div className="px-6 py-4 border-t border-secondary-200 flex items-center justify-between">
                        <p className="text-sm text-secondary-600">
                          Page {auditLogsPage} of {auditLogsTotalPages} ({auditLogsTotal} total)
                        </p>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setAuditLogsPage(p => Math.max(1, p - 1))}
                            disabled={auditLogsPage === 1}
                            className="px-4 py-2 border rounded-lg disabled:opacity-50"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => setAuditLogsPage(p => Math.min(auditLogsTotalPages, p + 1))}
                            disabled={auditLogsPage === auditLogsTotalPages}
                            className="px-4 py-2 border rounded-lg disabled:opacity-50"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Login History Tab */}
          {activeTab === 'login-history' && (
            <div className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="animate-spin text-primary-500" size={32} />
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-soft overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-secondary-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Admin</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Status</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Device</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">IP Address</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-secondary-200">
                        {loginHistory.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-12 text-center text-secondary-600">
                              No login history found
                            </td>
                          </tr>
                        ) : (
                          loginHistory.map((entry) => (
                            <tr key={entry.id} className="hover:bg-secondary-50 transition-colors">
                              <td className="px-6 py-4">
                                <div>
                                  <p className="font-semibold text-secondary-900">{entry.admin_name}</p>
                                  <p className="text-xs text-secondary-500">{entry.admin_email}</p>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                {getStatusBadge(entry.status)}
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-2">
                                  {getDeviceIcon(entry.device_type)}
                                  <div>
                                    <p className="text-sm text-secondary-900">{entry.browser || 'Unknown'}</p>
                                    <p className="text-xs text-secondary-500">{entry.os || 'Unknown OS'}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-secondary-600">{entry.ip_address}</span>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-secondary-600">
                                  {new Date(entry.login_at).toLocaleString()}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {loginHistoryTotalPages > 1 && (
                    <div className="px-6 py-4 border-t border-secondary-200 flex items-center justify-between">
                      <p className="text-sm text-secondary-600">
                        Page {loginHistoryPage} of {loginHistoryTotalPages} ({loginHistoryTotal} total)
                      </p>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setLoginHistoryPage(p => Math.max(1, p - 1))}
                          disabled={loginHistoryPage === 1}
                          className="px-4 py-2 border rounded-lg disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setLoginHistoryPage(p => Math.min(loginHistoryTotalPages, p + 1))}
                          disabled={loginHistoryPage === loginHistoryTotalPages}
                          className="px-4 py-2 border rounded-lg disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Sessions Tab */}
          {activeTab === 'sessions' && (
            <div className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="animate-spin text-primary-500" size={32} />
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-soft overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-secondary-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Admin</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Status</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Device</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">IP Address</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Last Activity</th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-secondary-700 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-secondary-200">
                        {sessions.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-6 py-12 text-center text-secondary-600">
                              No active sessions found
                            </td>
                          </tr>
                        ) : (
                          sessions.map((session) => (
                            <tr key={session.session_id} className="hover:bg-secondary-50 transition-colors">
                              <td className="px-6 py-4">
                                <div>
                                  <p className="font-semibold text-secondary-900">{session.admin_name}</p>
                                  <p className="text-xs text-secondary-500">{session.admin_email}</p>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                {getStatusBadge(session.status)}
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-2">
                                  {getDeviceIcon(session.device_type)}
                                  <div>
                                    <p className="text-sm text-secondary-900">{session.browser || 'Unknown'}</p>
                                    <p className="text-xs text-secondary-500">{session.os || 'Unknown OS'}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-secondary-600">{session.ip_address}</span>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-secondary-600">
                                  {new Date(session.last_activity).toLocaleString()}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center justify-end space-x-2">
                                  {session.status === 'active' && (
                                    <button
                                      onClick={() => handleRevokeSession(session.session_id)}
                                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                      title="Revoke Session"
                                    >
                                      <LogOut size={18} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {sessionsTotalPages > 1 && (
                    <div className="px-6 py-4 border-t border-secondary-200 flex items-center justify-between">
                      <p className="text-sm text-secondary-600">
                        Page {sessionsPage} of {sessionsTotalPages} ({sessionsTotal} total)
                      </p>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setSessionsPage(p => Math.max(1, p - 1))}
                          disabled={sessionsPage === 1}
                          className="px-4 py-2 border rounded-lg disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setSessionsPage(p => Math.min(sessionsTotalPages, p + 1))}
                          disabled={sessionsPage === sessionsTotalPages}
                          className="px-4 py-2 border rounded-lg disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Blocked IPs Tab */}
          {activeTab === 'blocked-ips' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setBlockIPForm({ ip_address: '', reason: '', block_duration_days: undefined });
                    setBlockIPModal(true);
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-all"
                >
                  <Ban size={20} />
                  <span>Block IP Address</span>
                </button>
              </div>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="animate-spin text-primary-500" size={32} />
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-soft overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-secondary-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">IP Address</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Reason</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Blocked By</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Blocked At</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Status</th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-secondary-700 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-secondary-200">
                        {blockedIPs.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-6 py-12 text-center text-secondary-600">
                              No blocked IPs found
                            </td>
                          </tr>
                        ) : (
                          blockedIPs.map((ip) => (
                            <tr key={ip.id} className="hover:bg-secondary-50 transition-colors">
                              <td className="px-6 py-4">
                                <code className="px-2 py-1 bg-secondary-100 text-secondary-700 rounded font-mono text-sm">
                                  {ip.ip_address}
                                </code>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-sm text-secondary-900">{ip.reason}</p>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-sm text-secondary-600">{ip.blocked_by_admin_name || 'System'}</p>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-secondary-600">
                                  {new Date(ip.blocked_at).toLocaleString()}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                {ip.is_active === 1 ? (
                                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Blocked</span>
                                ) : (
                                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Unblocked</span>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center justify-end space-x-2">
                                  {ip.is_active === 1 && (
                                    <button
                                      onClick={() => {
                                        setSelectedIP(ip.ip_address);
                                        setUnblockIPModal(true);
                                      }}
                                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-all"
                                      title="Unblock IP"
                                    >
                                      <Unlock size={18} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {blockedIPsTotalPages > 1 && (
                    <div className="px-6 py-4 border-t border-secondary-200 flex items-center justify-between">
                      <p className="text-sm text-secondary-600">
                        Page {blockedIPsPage} of {blockedIPsTotalPages} ({blockedIPsTotal} total)
                      </p>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setBlockedIPsPage(p => Math.max(1, p - 1))}
                          disabled={blockedIPsPage === 1}
                          className="px-4 py-2 border rounded-lg disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setBlockedIPsPage(p => Math.min(blockedIPsTotalPages, p + 1))}
                          disabled={blockedIPsPage === blockedIPsTotalPages}
                          className="px-4 py-2 border rounded-lg disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Failed Login Attempts Tab */}
          {activeTab === 'failed-logins' && (
            <div className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="animate-spin text-primary-500" size={32} />
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-soft overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-secondary-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Email</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">IP Address</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Failure Reason</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">User Agent</th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Attempted At</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-secondary-200">
                        {failedAttempts.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-12 text-center text-secondary-600">
                              No failed login attempts found
                            </td>
                          </tr>
                        ) : (
                          failedAttempts.map((attempt) => (
                            <tr key={attempt.id} className="hover:bg-secondary-50 transition-colors">
                              <td className="px-6 py-4">
                                <span className="text-sm text-secondary-900">{attempt.email || attempt.attempted_email || 'N/A'}</span>
                              </td>
                              <td className="px-6 py-4">
                                <code className="px-2 py-1 bg-secondary-100 text-secondary-700 rounded font-mono text-sm">
                                  {attempt.ip_address}
                                </code>
                              </td>
                              <td className="px-6 py-4">
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                                  {attempt.failure_reason}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-secondary-600">{attempt.user_agent || 'N/A'}</span>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-secondary-600">
                                  {new Date(attempt.attempted_at).toLocaleString()}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {failedAttemptsTotalPages > 1 && (
                    <div className="px-6 py-4 border-t border-secondary-200 flex items-center justify-between">
                      <p className="text-sm text-secondary-600">
                        Page {failedAttemptsPage} of {failedAttemptsTotalPages} ({failedAttemptsTotal} total)
                      </p>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setFailedAttemptsPage(p => Math.max(1, p - 1))}
                          disabled={failedAttemptsPage === 1}
                          className="px-4 py-2 border rounded-lg disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setFailedAttemptsPage(p => Math.min(failedAttemptsTotalPages, p + 1))}
                          disabled={failedAttemptsPage === failedAttemptsTotalPages}
                          className="px-4 py-2 border rounded-lg disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Block IP Modal */}
      {blockIPModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-secondary-900">Block IP Address</h2>
                <button
                  onClick={() => {
                    setBlockIPModal(false);
                    setBlockIPForm({ ip_address: '', reason: '', block_duration_days: undefined });
                  }}
                  className="text-secondary-500 hover:text-secondary-700"
                >
                  <X size={24} />
                </button>
              </div>
              <form onSubmit={handleBlockIP} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    IP Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={blockIPForm.ip_address}
                    onChange={(e) => setBlockIPForm({...blockIPForm, ip_address: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none font-mono"
                    placeholder="192.168.1.1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    minLength={10}
                    value={blockIPForm.reason}
                    onChange={(e) => setBlockIPForm({...blockIPForm, reason: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    rows={3}
                    placeholder="Enter reason for blocking (minimum 10 characters)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Block Duration (Days) - Optional
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={blockIPForm.block_duration_days || ''}
                    onChange={(e) => setBlockIPForm({...blockIPForm, block_duration_days: e.target.value ? parseInt(e.target.value) : undefined})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="Leave empty for permanent block"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setBlockIPModal(false);
                      setBlockIPForm({ ip_address: '', reason: '', block_duration_days: undefined });
                    }}
                    className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="px-6 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-all disabled:opacity-50"
                  >
                    {processing ? 'Blocking...' : 'Block IP'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Unblock IP Modal */}
      {unblockIPModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Unlock className="text-green-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-secondary-900">Unblock IP Address</h2>
                  <p className="text-sm text-secondary-600">This will allow access from this IP address</p>
                </div>
              </div>
              <p className="text-secondary-700 mb-6">
                Are you sure you want to unblock <strong>{selectedIP}</strong>?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setUnblockIPModal(false);
                    setSelectedIP('');
                  }}
                  className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUnblockIP}
                  disabled={processing}
                  className="px-6 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-all disabled:opacity-50"
                >
                  {processing ? 'Unblocking...' : 'Unblock IP'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityAuditManagement;

